import { logEvent } from '@/config/analytics';
import { getClientId } from '@/transport/client-id';
import { TurnAccess } from '@/transport/turn';
import { WsChannel, type ChannelClosure } from '@/transport/ws-channel';
import { browserProfile } from './browser';
import { CallSession } from './call-session';
import { CALL_LIMIT_MS } from './call-duration';
import { observeLiveness } from './liveness-signals';
import { MediaController, MediaFailure } from './media';
import { isValidRoomId } from './room';
import { initialCallState, type CallEndReason, type CallState } from './types';

/**
 * Звʼязує транспорт, медіа й сесію в один стан для інтерфейсу.
 *
 * Це єдине місце, де вони зустрічаються; компоненти читають знімок стану і
 * кличуть команди, але самі до сокета чи `RTCPeerConnection` не дістають.
 */
export interface AppSnapshot extends CallState {
  micEnabled: boolean;
  videoEnabled: boolean;
  screenSharing: boolean;
  screenShareSupported: boolean;
  /** Дзеркалення відео співрозмовника — на час сесії, не зберігається. */
  mirrorRemote: boolean;
  /** Як вписувати відео співрозмовника: заповнити кадр чи показати цілком. */
  remoteFit: 'cover' | 'contain';
  /** Ігнорувати команди віддаленого керування від співрозмовника. */
  remoteControlBlocked: boolean;
  remoteStream: MediaStream | null;
  localStream: MediaStream | null;
  remainingMs: number;
  /** Помилка для показу користувачу: ключ словника. */
  errorKey: string | null;
  /** Показати пояснення про дозволи замість банера. */
  permissionIssue: 'denied' | 'unavailable' | null;
}

type Listener = (snapshot: AppSnapshot) => void;

export class AppController {
  private state: CallState = { ...initialCallState };
  private micEnabled = true;
  private videoEnabled = true;
  private screenSharing = false;
  private mirrorRemote = false;
  /**
   * Дефолт саме `contain`, а не `cover`.
   *
   * Веб-камери дають інші співвідношення сторін, ніж телефон у портреті, тож
   * `cover` обрізає співрозмовнику голову або половину кадру. Мобільний
   * клієнт дійшов того самого висновку самостійно: `_shouldUseContainForIOS()`
   * у `remote_video_widget.dart` перемикається на contain, щойно співрозмовник
   * приходить із браузера. Перемкнути на cover можна в розширеній панелі.
   */
  private remoteFit: 'cover' | 'contain' = 'contain';
  private remoteControlBlocked = false;
  private remoteStream: MediaStream | null = null;
  private remainingMs = CALL_LIMIT_MS;
  private errorKey: string | null = null;
  private permissionIssue: 'denied' | 'unavailable' | null = null;
  private cachedSnapshot: AppSnapshot | null = null;

  private readonly listeners = new Set<Listener>();
  private readonly turn = new TurnAccess();
  private readonly media: MediaController;
  private channel: WsChannel | null = null;
  private session: CallSession | null = null;
  private stopLiveness: (() => void) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private deadline = 0;
  private lastRoomId: string | null = null;

  constructor() {
    this.media = new MediaController(undefined, () => {
      void this.stopScreenShare();
    });
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    // Слухач НЕ викликається одразу: початкове значення читається через
    // snapshot(), і зайвий виклик тут перетворює підписку на цикл оновлень.
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Знімок стану.
   *
   * ⚠️ Обʼєкт кешується і перестворюється лише в `emit()`. Повертати щоразу
   * новий обʼєкт не можна: `useSyncExternalStore` порівнює знімки за
   * посиланням, і новий обʼєкт на кожен виклик означає нескінченний ререндер.
   */
  snapshot(): AppSnapshot {
    this.cachedSnapshot ??= this.buildSnapshot();
    return this.cachedSnapshot;
  }

  private buildSnapshot(): AppSnapshot {
    return {
      ...this.state,
      micEnabled: this.micEnabled,
      videoEnabled: this.videoEnabled,
      screenSharing: this.screenSharing,
      screenShareSupported: browserProfile().supportsScreenShare,
      mirrorRemote: this.mirrorRemote,
      remoteFit: this.remoteFit,
      remoteControlBlocked: this.remoteControlBlocked,
      remoteStream: this.remoteStream,
      localStream: this.media.stream,
      remainingMs: this.remainingMs,
      errorKey: this.errorKey,
      permissionIssue: this.permissionIssue,
    };
  }

  private emit(): void {
    this.cachedSnapshot = this.buildSnapshot();
    for (const listener of this.listeners) listener(this.cachedSnapshot);
  }

  private setPhase(state: Partial<CallState>): void {
    this.state = { ...this.state, ...state };
    this.emit();
  }

  /**
   * Вхід у кімнату. Викликається ЛИШЕ з дії користувача: у Safari
   * `getUserMedia` поза жестом відхиляється, тож автоматичного входу за
   * посиланням тут немає навмисно.
   */
  async joinRoom(roomId: string): Promise<void> {
    if (!isValidRoomId(roomId)) {
      this.fail('invalid-room', 'roomIdValidationError');
      return;
    }

    this.errorKey = null;
    this.permissionIssue = null;
    this.lastRoomId = roomId;
    this.setPhase({ phase: 'connecting', roomId, endReason: null });

    try {
      await this.media.acquire();
    } catch (error) {
      const kind = error instanceof MediaFailure ? error.kind : 'unavailable';
      // Відмова в дозволі й недоступний пристрій ведуть до різних пояснень:
      // у першому випадку треба змінити дозвіл, у другому — перевірити камеру.
      this.permissionIssue = kind;
      this.lastRoomId = roomId;
      this.fail(kind === 'denied' ? 'media-denied' : 'media-unavailable', null);
      return;
    }

    this.micEnabled = true;
    this.videoEnabled = true;
    logEvent('call_started');

    // Гріємо кеш креденшлів одразу на вході в кімнату, не чекаючи співрозмовника.
    // Інакше доказ роботи почав би рахуватися рівно в момент його появи й
    // відкладав би пропозицію на час обчислення. Запит усе одно один на строк
    // дії креденшлів — кеш усередині TurnAccess цього не міняє.
    void this.turn.getConfiguration();

    this.openChannel(roomId);
    this.startTimer();
    this.observeBrowserSignals();
  }

  private openChannel(roomId: string): boolean {
    const clientId = getClientId();
    const profile = browserProfile();

    const channel = new WsChannel({
      onOpen: () => {
        this.session?.announce(profile.isWebKit, profile.deviceType);
        this.setPhase({ phase: 'waiting' });
        logEvent('room_established', { device_type: profile.deviceType });
      },
      onMessage: (message) => {
        void this.session?.handleMessage(message);
      },
      onClose: (closure: ChannelClosure) => {
        void this.session?.handleChannelClosure(closure);
      },
    });
    this.channel = channel;

    this.session ??= new CallSession({
      clientId,
      send: (payload) => {
        this.channel?.send(payload);
      },
      reconnectChannel: () => this.reopenChannel(roomId),
      getIceConfiguration: async () => {
        const config = await this.turn.getConfiguration();
        logEvent('ice_selected', { has_relay: config.hasRelay });
        return { iceServers: [...config.iceServers] };
      },
      getLocalStream: () => this.media.stream,
      onPhase: (phase, reason) => {
        this.setPhase(reason === undefined ? { phase } : { phase, endReason: reason });
        if (phase === 'ended') this.releaseAfterEnd(reason);
      },
      onRemoteStream: (stream) => {
        this.remoteStream = stream;
        this.setPhase({ hasRemoteVideo: stream !== null });
      },
      onPeerIsBot: (isBot) => {
        this.setPhase({ peerIsBot: isBot });
      },
      onPeerMedia: (peerMedia) => {
        this.setPhase({ peerMedia });
      },
      onRemoteControl: (action, value) => {
        // Блокування — це саме про чужі команди: власні кнопки працюють далі.
        if (this.remoteControlBlocked) return;
        logEvent('media_toggle', { kind: action, enabled: value, source: 'remote' });
        if (action === 'mic') this.setMicEnabled(value);
        else this.setVideoEnabled(value);
      },
      onReconnectAttempt: (attempt) => {
        this.setPhase({ reconnectAttempt: attempt });
        logEvent('reconnection_attempt', { attempt });
      },
    });

    channel.connect(roomId, clientId);
    return true;
  }

  private async reopenChannel(roomId: string): Promise<boolean> {
    this.channel?.close();
    this.channel = null;
    return Promise.resolve(this.openChannel(roomId));
  }

  private observeBrowserSignals(): void {
    this.stopLiveness?.();
    this.stopLiveness = observeLiveness(() => {
      // Повернення до вкладки й відновлення мережі — той самий сигнал, що й
      // стан зʼєднання: guard усередині сесії відсіє зайве.
      if (this.channel?.isOpen === true) return;
      void this.session?.handleConnectionLoss();
    });
  }

  private startTimer(): void {
    this.stopTimer();
    this.deadline = Date.now() + CALL_LIMIT_MS;
    this.remainingMs = CALL_LIMIT_MS;
    this.timer = setInterval(() => {
      this.remainingMs = Math.max(0, this.deadline - Date.now());
      if (this.remainingMs === 0) {
        this.endCall('time-expired');
        return;
      }
      this.emit();
    }, 1000);
  }

  private stopTimer(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  setMicEnabled(enabled: boolean): void {
    this.micEnabled = enabled;
    logEvent('media_toggle', { kind: 'mic', enabled });
    this.media.setMicEnabled(enabled);
    this.channel?.send({
      type: 'media_state',
      micEnabled: enabled,
      videoEnabled: this.videoEnabled,
    });
    this.emit();
  }

  setVideoEnabled(enabled: boolean): void {
    this.videoEnabled = enabled;
    logEvent('media_toggle', { kind: 'video', enabled });
    this.media.setVideoEnabled(enabled);
    this.channel?.send({
      type: 'media_state',
      micEnabled: this.micEnabled,
      videoEnabled: enabled,
    });
    this.emit();
  }

  async startScreenShare(): Promise<void> {
    if (!browserProfile().supportsScreenShare) return;
    const track = await this.media.startScreenShare();
    // null означає, що користувач скасував вибір джерела — це не помилка.
    if (!track) return;
    await this.session?.replaceVideoTrack(track);
    logEvent('screen_share_started');
    this.screenSharing = true;
    this.emit();
  }

  /**
   * Повертає передавання камери. Викликається і кнопкою застосунку, і
   * системною кнопкою браузера — шлях мусить бути один.
   */
  async stopScreenShare(): Promise<void> {
    if (!this.screenSharing) return;
    const cameraTrack = this.media.stopScreenShare();
    await this.session?.replaceVideoTrack(cameraTrack);
    logEvent('screen_share_stopped');
    this.screenSharing = false;
    this.emit();
  }

  toggleMirror(): void {
    this.mirrorRemote = !this.mirrorRemote;
    this.emit();
  }

  toggleRemoteFit(): void {
    this.remoteFit = this.remoteFit === 'cover' ? 'contain' : 'cover';
    this.emit();
  }

  toggleRemoteControlBlocked(): void {
    this.remoteControlBlocked = !this.remoteControlBlocked;
    this.emit();
  }

  endCall(reason: CallEndReason = 'local-hangup'): void {
    this.session?.end(reason);
    if (!this.session) this.releaseAfterEnd(reason);
  }

  /** Спільний хвіст завершення: виконується будь-яким шляхом виходу з дзвінка. */
  private releaseAfterEnd(reason?: CallEndReason): void {
    logEvent('call_ended', { reason: reason ?? 'local-hangup' });
    if (reason === 'reconnect-failed') logEvent('reconnection_failed');
    if (reason === 'connection-failed') logEvent('connection_error');
    this.stopTimer();
    this.stopLiveness?.();
    this.stopLiveness = null;
    this.channel?.close();
    this.channel = null;
    this.session = null;
    this.remoteStream = null;
    this.screenSharing = false;
    this.mirrorRemote = false;
    this.remoteFit = 'contain';
    this.remoteControlBlocked = false;
    this.media.release();
    this.state = { ...initialCallState, phase: 'ended', endReason: reason ?? 'local-hangup' };
    this.emit();
  }

  private fail(reason: CallEndReason, errorKey: string | null): void {
    this.errorKey = errorKey;
    this.setPhase({ phase: 'ended', endReason: reason });
    this.releaseAfterEnd(reason);
  }

  /** Повторна спроба після пояснення про дозволи — тим самим жестом користувача. */
  async retryAfterPermissionIssue(): Promise<void> {
    const roomId = this.lastRoomId;
    this.permissionIssue = null;
    this.emit();
    if (roomId !== null) await this.joinRoom(roomId);
  }

  dismissPermissionIssue(): void {
    this.permissionIssue = null;
    this.emit();
  }

  reset(): void {
    this.errorKey = null;
    this.permissionIssue = null;
    this.state = { ...initialCallState };
    this.remainingMs = CALL_LIMIT_MS;
    this.emit();
  }

  dismissError(): void {
    this.errorKey = null;
    this.emit();
  }
}
